import { JSX } from 'react';

/**
 * A small map preview for an address the assistant has cited.
 *
 * OpenStreetMap's own embed, which needs no key, no account and no billing. It takes a bounding
 * box rather than an address, so the Operator resolves the coordinates through Nominatim and
 * caches them in Redis; the URL arrives here ready to use.
 *
 * There is deliberately no Google Maps path. Carrying two map providers for an occasional
 * address preview is more moving parts than the feature is worth.
 */

/** OpenStreetMap hosts the embed itself, so only its own frames are ever rendered. */
const EMBED_HOST: string = 'www.openstreetmap.org';
const EMBED_PATH: string = '/export/embed.html';

/**
 * Whether a URL is an OpenStreetMap embed.
 *
 * The href comes from a model-written reply, so it is checked rather than trusted: anything else
 * is rendered as an ordinary link and never as a frame.
 */
export function isMapEmbedUrl(href: string): boolean {
    try {
        const url: URL = new URL(href);
        return url.protocol === 'https:'
            && url.hostname === EMBED_HOST
            && url.pathname === EMBED_PATH;
    } catch {
        return false;
    }
}

/**
 * The full OpenStreetMap page for an embed, derived from the marker the embed already carries.
 *
 * Deriving it means the reply only ever has to contain one URL. An embed href on its own is a
 * bare frame with no controls, so it is not somewhere to send a person directly.
 */
export function mapPageUrl(embedHref: string): string {
    try {
        const marker: string | null = new URL(embedHref).searchParams.get('marker');
        if (marker === null) {
            return 'https://www.openstreetmap.org/';
        }
        const [lat, lon] = marker.split(',');
        return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`;
    } catch {
        return 'https://www.openstreetmap.org/';
    }
}

export const MapCard: (props: { href: string, label: string }) => JSX.Element =
    ({ href, label }): JSX.Element => (
        <span className="message__map">
            <iframe
                className="map__frame"
                src={ href }
                title={ 'Map showing ' + label }
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
            />
            <a className="map__open" href={ mapPageUrl(href) } target="_blank" rel="noreferrer">
                Open larger map
            </a>
        </span>
    );
